import { Button, ButtonRow, Image } from '@timelyapp/react';
import classNames from 'classnames';
import DisabledMask from 'components/DisabledMask';
import type { DropdownOption } from 'components/DropdownListWithOptions';
import DurationFormat from 'components/DurationFormat';
import { DragHandle, Ellipsis } from 'components/Icons';
import Label from 'components/Label';
import { PopoverProjectPickerWithOptions } from 'components/popovers/PopoverProjectPickerWithOptions';
import { useCurrentSurface } from 'components/Surface';
import { EMPTY_ARRAY } from 'constants/defaults';
import { DURATION_FORMAT } from 'constants/memory';
import type { DropActionAreaSpecifier, MemoryDndItem } from 'constants/memory-dnd';
import MemoryDnD from 'constants/memory-dnd';
import React, { useEffect, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import FontAwesome from 'react-fontawesome';
import type { toggleHourTimestampFromMemory } from 'redux/modules/hours.actions';
import type { MappedAction } from 'types/redux';
import type { MemoryGroup as MemoryGroupType, ValidGroupStatus } from 'types/timeline';
import { OperatorMode } from 'types/timeline';
import { getSingleFromToViaTimestamps } from 'utils/timestamps';
import { notNull } from 'utils/typesafe';
import styles from './MemoryGroup.module.scss';
import { MemoryItem } from './MemoryItem';

interface MemoryGroupProps {
  toggleSelectEntryIds?: (entry_ids: Array<number>) => unknown;
  toggleExpandGroupRef?: (groupRef: string) => unknown;
  toggleExpandEntryId?: (entryId: number) => unknown;
  markEntryIdsAsSpam?: (ids: Array<number>) => unknown;
  markEntryIdsAsNotSpam?: (ids: Array<number>) => unknown;
  predictEntryIdsToProjectId?: (args: {
    entryIds: Array<number> | number;
    projectId: number;
  }) => unknown;
  deleteEntryIds?: (ids: Array<number>) => unknown;
  onDisassociateMemoryIds?: (ids: Array<number>) => unknown;
  removeHourDraftEntryIds?: (ids: Array<number>) => void;
  group: MemoryGroupType;
  groupSection: ValidGroupStatus;
  showDebugInfo?: boolean;
  // Duplicating this type seems fair since we need to add support for any modes, right?
  operatorMode: OperatorMode;
  dragSource: DropActionAreaSpecifier;
  isLocked?: boolean;
  setItemSize?: React.Dispatch<React.SetStateAction<Record<number, number>>>;
  itemSizeList?: Record<number, number>;
  groupIndex?: number;
  /** Timestamp-focused mode via TIC */
  hasEnabledTic?: boolean;
  toggleHourTimestampFromMemory: MappedAction<typeof toggleHourTimestampFromMemory>;
}

export const MemoryGroup = (props: MemoryGroupProps) => {
  const {
    group,
    groupSection,
    showDebugInfo,
    operatorMode,
    markEntryIdsAsNotSpam,
    markEntryIdsAsSpam,
    toggleSelectEntryIds,
    toggleHourTimestampFromMemory,
    predictEntryIdsToProjectId,
    deleteEntryIds,
    toggleExpandGroupRef,
    toggleExpandEntryId,
    dragSource,
    isLocked,
    setItemSize,
    groupIndex,
    itemSizeList,
  } = props;
  const onSurface = useCurrentSurface();
  const heightRef = useRef<HTMLDivElement>(null);
  const [actAsHovered, setActAsHovered] = useState(false);
  const [isHoveringCaret, setHoveringCaret] = useState(false);
  const [_dragProps, dragRef] = useDrag(() => {
    const dragItem: MemoryDndItem = {
      memoryIds: group.memories.map(memory => memory.id),
      source: dragSource,
    };
    return {
      type: MemoryDnD.TYPE,
      canDrag: !isLocked,
      item: dragItem,
    };
  }, [group, dragSource, isLocked]);

  const handleToggleSpam = () => {
    if (props.hasEnabledTic) {
      console.warn('🪵 TODO: handleToggleSpam is not yet supported');
      return;
    }
    const ids = group.memories.map(({ id }) => id);
    if (group.isSpam) {
      markEntryIdsAsNotSpam?.(ids);
    } else {
      markEntryIdsAsSpam?.(ids);
    }
  };

  const handleCheckboxChange = () => {
    const allChecked = group.memories.every(({ isChecked }) => isChecked);
    // If all memories are checked, send everything. Otherwise, send only unchecked
    const memoriesDiff = group.memories.filter(memory => memory.isChecked === allChecked);

    // Timespan-mode only in TIC
    if (props.hasEnabledTic) {
      const memoriesAsTimestamps = memoriesDiff.map(({ from, to }) => ({
        from,
        to,
        entry_ids: [], // DO NOT USE, TIC specific, adding just to shut up typescript
      }));
      const { from, to } = getSingleFromToViaTimestamps(memoriesAsTimestamps);

      toggleHourTimestampFromMemory(group.isChecked ? 'remove' : 'add', [
        {
          from,
          to,
          entry_ids: [], // DO NOT USE, TIC specific, adding just to shut up typescript
        },
      ]);
    } else if (toggleSelectEntryIds != null) {
      const ids = memoriesDiff.map(({ id }) => id);
      toggleSelectEntryIds(ids);
    }
  };

  const handleDeselect = () => {
    const ids = group.memories.map(({ id }) => id);
    if (props.hasEnabledTic) {
      console.warn('🪵 handleDeselect should not fire, missing functionality?');
      return;
    }

    if (isWithHourMode) {
      return props.removeHourDraftEntryIds?.(ids);
    }

    props.onDisassociateMemoryIds?.(ids);
  };

  const handleSelectProjectPrediction = (projectId: number | null) => {
    if (props.hasEnabledTic) {
      console.log('🪵 TODO: handleSelectProjectPrediction does NOT YET support TIC');
      return;
    }
    if (projectId) {
      const entryIds = group.memories.map(({ id }) => id);
      if (predictEntryIdsToProjectId) {
        predictEntryIdsToProjectId({
          entryIds,
          projectId,
        });
      }
    }
  };

  const handleDropdownOptionClick = ({ action }: DropdownOption) => {
    if (props.hasEnabledTic) {
      console.log('🪵 TODO: handleDropdownOptionClick does NOT YET support TIC');
      return;
    }
    if (action === 'delete') {
      const ids = group.memories.map(({ id }) => id);
      if (deleteEntryIds) {
        deleteEntryIds(ids);
      }
    } else if (action === 'toggleSpam') {
      handleToggleSpam();
    }
  };

  const generateOptionsDropdown = (customButton?: React.ReactElement) => (
    <PopoverProjectPickerWithOptions
      onClose={() => setActAsHovered(false)}
      onOpen={() => setActAsHovered(true)}
      position="bottom-end"
      predictedProjects={group.projects}
      value={predictedProjectId}
      onOptionClick={handleDropdownOptionClick}
      options={dropdownOptions}
      showProjects={!showInboxActions && !disableProjectSelect}
      onChange={handleSelectProjectPrediction}
      target={
        customButton || (
          <Button
            className={styles.toggleOptionsButton}
            size="small"
            priority="medium"
            caret={false}
          >
            <Ellipsis />
          </Button>
        )
      }
    />
  );

  const isInboxMode = operatorMode === OperatorMode.memoryInbox;
  const isWithHourMode = operatorMode === OperatorMode.withHourDraft;
  const isWithHourModeSelected =
    operatorMode === OperatorMode.withHourDraft && groupSection === 'selected';
  const isCompactMode = (!isInboxMode && !isWithHourMode) || group.isChecked;
  const disableProjectSelect = isCompactMode;
  const showInboxActions = isInboxMode && !group.isSpam && !disableProjectSelect;

  const canIgnoreRestore = markEntryIdsAsSpam != null && markEntryIdsAsNotSpam != null;
  const showInlineIgnoreButton = !isCompactMode && groupSection !== 'selected';

  // The actual numbers here aren't as expected,
  // look into this via https://app.clubhouse.io/timelyapp/story/13095
  const showSelectedDuration =
    groupSection === 'selected' || group.isChecked || group.isPartlyChecked;

  const calculatedDurationMinutes = showSelectedDuration
    ? group.durationForSelected.total_minutes
    : group.durationForUnselected.total_minutes;

  const containerClass = classNames(styles.container, styles[`on-surface-${onSurface}`], {
    [styles.isHovered]: actAsHovered,
    [styles.isChecked]: isWithHourModeSelected,
    [styles.isLogged]: group.isLogged,
    [styles.isWithHourMode]: isWithHourMode,
    [styles.isInboxMode]: isInboxMode,
    [styles.isCompactMode]: isCompactMode || isWithHourModeSelected,
  });
  const groupClass = classNames(styles.group, {
    [styles.isChecked]: group.isChecked || group.isPartlyChecked,
    [styles.isWithHourMode]: isWithHourMode,
  });

  const predictedProjectId = group.projects[0]?.base.id ?? null;

  const debugOptions: Array<DropdownOption> = !showDebugInfo
    ? EMPTY_ARRAY
    : [
        {
          type: 'divider',
        },
        {
          action: 'none',
          name: 'Debug (Timely employees only)',
          type: 'header',
        },
        {
          disabled: true,
          name: `${group.importancePercent} importance`,
        },
        {
          disabled: true,
          name: `Raw duration: ${group.duration.formatted}`,
        },
        {
          disabled: true,
          name: group.isSpam ? 'Ignored: Yes' : 'Ignored: No',
        },
        {
          disabled: true,
          name: group.isLogged ? 'Already Logged: Yes' : 'Already Logged: No',
        },
        {
          disabled: true,
          name: group.isPredicted ? 'Part of Suggested Hour: Yes' : 'Part of Suggested Hour: No',
        },
        {
          disabled: true,
          name: predictedProjectId
            ? `${Math.round(group.projects[0].accuracy * 100)}% accurate to ${
                group.projects[0]?.base.name
              }`
            : 'No predicted project',
        },
        {
          disabled: true,
          // action: 'delete',
          name: group.currentProjectPrediction
            ? `${group.currentProjectPredictionPercent} accuracy to project in HourDraft`
            : 'HourEditor not active -- cannot estimate accuracy',
        },
      ];
  const dropdownOptions: Array<DropdownOption> = [
    canIgnoreRestore && !showInlineIgnoreButton
      ? ({
          action: 'toggleSpam',
          name: group.isSpam ? 'Restore' : 'Ignore',
        } as const)
      : null,
    deleteEntryIds != null
      ? ({
          action: 'delete',
          name: 'Delete',
          confirm: true,
          confirmMessage: 'Delete memory? This cannot be undone',
        } as const)
      : null,
    ...debugOptions,
  ].filter(notNull);

  const toggleExpanded = () => {
    if (toggleExpandGroupRef) {
      toggleExpandGroupRef(group.ref);
    }
  };

  useEffect(() => {
    if (heightRef.current && groupIndex !== undefined && groupIndex >= 0) {
      const height = heightRef?.current?.offsetHeight;

      if (!itemSizeList?.[groupIndex] || height !== itemSizeList[groupIndex]) {
        setItemSize?.(prev => ({ ...prev, [groupIndex]: height }));
        return;
      }
    }
  }, [groupIndex, heightRef?.current?.offsetHeight, itemSizeList, setItemSize]);

  const shouldToggleExpanded = isCompactMode || group.isExpanded || isHoveringCaret;
  const showTransparentLockedState = isCompactMode && !isWithHourMode && props.isLocked;
  const onClickBox = shouldToggleExpanded ? toggleExpanded : handleCheckboxChange;
  return (
    <div
      className={containerClass}
      key={group.title}
      ref={dragRef}
      data-testid="memory_group_container"
    >
      <div ref={heightRef}>
        <DisabledMask
          active={!!props.isLocked}
          style={{
            backgroundColor: showTransparentLockedState ? 'transparent' : undefined,
          }}
        >
          <div className={groupClass}>
            <div className={styles.clickToSelectContent} onClick={onClickBox}>
              <div className={styles.checkboxColumn}>
                {!props.isLocked ? (
                  <div className={styles.dragHandle}>
                    <DragHandle />
                  </div>
                ) : null}
              </div>
              <div className={styles.iconContainer}>
                <Image
                  src={group.icon}
                  fallback={group.fallbackIcon}
                  className={classNames(styles.icon, 'fs-exclude')}
                />
              </div>
              <div className={styles.content}>
                <span
                  className={classNames(styles.title, 'fs-mask')}
                  data-testid="memory_group_title"
                >
                  {group.title}
                  {group.ui.hasMemories && (
                    <span
                      className={styles.compactModeCaret}
                      onMouseEnter={() => setHoveringCaret(true)}
                      onMouseLeave={() => setHoveringCaret(false)}
                    >
                      <FontAwesome name={group.isExpanded ? 'caret-down' : 'caret-right'} />
                    </span>
                  )}
                </span>
                <div className={classNames(styles.body, 'fs-mask')} data-testid="memory_group_body">
                  {group.status.isVisible && (
                    <React.Fragment>
                      <small
                        className={classNames(
                          styles.status,
                          styles[`variant-${group.status.variant}`]
                        )}
                      >
                        {group.status.message}
                      </small>
                      {!!group.subtitle && <small>&nbsp;&bull;&nbsp;</small>}
                    </React.Fragment>
                  )}
                  {!!group.subtitle && <small>{group.subtitle}</small>}
                </div>
              </div>
            </div>

            <div className={styles.hiddenOrVisibleContainer}>
              <div className={styles.invisibleOnHover}>
                {group.isSpam && (
                  <Label variant="muted" uppercase={false}>
                    Ignored
                  </Label>
                )}
                &nbsp;
              </div>
              <ButtonRow
                className={styles.visibleOverlayOnHoverToolbar}
                data-testid="memory_group_options"
              >
                {canIgnoreRestore && showInlineIgnoreButton && (
                  <Button size="small" priority="medium" onClick={handleToggleSpam}>
                    {group.isSpam ? 'Restore' : 'Ignore'}
                  </Button>
                )}
                {!!dropdownOptions.length && (
                  <>
                    {generateOptionsDropdown()}
                    <Button
                      priority="medium"
                      size="small"
                      className={styles.toggleExpandedButton}
                      onClick={toggleExpanded}
                    >
                      <FontAwesome
                        name={group.isExpanded ? 'caret-down' : 'caret-right'}
                        data-testid="memory_group_expand"
                      />
                    </Button>
                  </>
                )}
              </ButtonRow>
            </div>

            <div
              className={styles.timestamp}
              onClick={toggleExpanded}
              data-testid="memory_group_full_duration"
            >
              {group.ui.showTimestamp && <span>{group.memories.length} entries</span>}
              {group.ui.showDuration && (
                <DurationFormat
                  format={DURATION_FORMAT}
                  trim
                  stopTrim="m"
                  value={group.duration.total_minutes}
                />
              )}
              {group.ui.showCalculatedDuration && (
                <DurationFormat
                  format={DURATION_FORMAT}
                  trim
                  stopTrim="m"
                  value={calculatedDurationMinutes}
                />
              )}
              {group.ui.hasMemories && (
                <FontAwesome name={group.isExpanded ? 'caret-down' : 'caret-right'} />
              )}
            </div>
            <div
              className={styles.deselectIcon}
              onClick={handleDeselect}
              data-testid="memory_group_deselect"
            >
              ✕
            </div>
          </div>
          <div
            className={classNames({
              [styles.individualMemoriesContainer]: group.isExpanded,
              [styles.isInboxMode]: isInboxMode,
            })}
          >
            {group.isExpanded &&
              group.memories.map(memory => {
                return (
                  <MemoryItem
                    hasEnabledTic={props.hasEnabledTic}
                    key={memory.id}
                    toggleExpandEntryId={toggleExpandEntryId}
                    memory={memory}
                    dragSource={dragSource}
                    selectedGroupSection={groupSection === 'selected'}
                    operatorMode={operatorMode}
                  />
                );
              })}
          </div>
        </DisabledMask>
      </div>
    </div>
  );
};
MemoryGroup.defaultProps = {
  dragSource: { area: MemoryDnD.AREA.MemoryInbox },
};
